import { UiOption } from '@/lib/helpers';
import { PropModel } from '../prop.model';
import {
  ControlActionValue,
  ControlCategory,
  ControlCategoryActionMapping,
  getSettingOptions,
} from './controls';

export type ContentControlType =
  | 'high-risk'
  | 'prompt-injection'
  | 'toxicity'
  | 'code'
  | 'confidential-data'
  | 'file-upload';

export type ContentControlEntity = {
  type: ContentControlType;
  action: ControlActionValue;
};

export class ContentControlConfig extends PropModel {
  entities: ContentControlEntity[];

  constructor(props: Record<string, unknown>) {
    super();
    const data = (props || {}) as Record<keyof ContentControlConfig, unknown>;
    this.entities = (data.entities as ContentControlEntity[]) || [];
  }
}

export type ContentControlAction = {
  label: string;
  value: ControlActionValue;
  level: number;
  category: ControlCategory;
};

type ContentControlSetting = {
  type: ContentControlType;
  label: string;
  description: string;
  actions: ControlActionValue[];
  comingSoonActions?: ControlActionValue[];
  defaultAction: ControlActionValue;
};

// Available controls shown to the user, with labels
export const ContentControlSettings: ContentControlSetting[] = [
  {
    type: 'high-risk',
    label: 'High-risk Request',
    description:
      'Requests that match multiple high-risk heuristics, including the related public service risk',
    actions: ['ignore', 'log', 'warn'],
    comingSoonActions: ['block'],
    defaultAction: 'ignore',
  },
  {
    type: 'confidential-data',
    label: 'Confidential Data',
    description:
      'Evidence of sensitive, private, or proprietary information which could be exposed or misused if retained and processed',
    actions: ['ignore', 'log', 'warn'],
    comingSoonActions: ['block'],
    defaultAction: 'ignore',
  },
  {
    type: 'code',
    label: 'Programming Language',
    description: 'Code, markup, or other programming languages',
    actions: ['ignore', 'log', 'warn', 'tag', 'mask', 'delete', 'block'],
    defaultAction: 'ignore',
  },
  {
    type: 'toxicity',
    label: 'Harmful Content',
    description:
      'Offensive or inappropriate content that may lead to responses which are unwanted or dangerous',
    actions: ['ignore', 'log', 'warn', 'block'],
    defaultAction: 'ignore',
  },
  {
    type: 'prompt-injection',
    label: 'Prompt Injection',
    description: 'User input designed to manipulate a model and override safeguards',
    actions: ['ignore', 'log', 'warn', 'block'],
    defaultAction: 'ignore',
  },
  {
    type: 'file-upload',
    label: 'File Upload',
    description: 'Requests that contain file attachments or uploads',
    actions: ['ignore', 'log', 'block'],
    defaultAction: 'ignore',
  },
];

export const getContentControlActionOptions = (): Map<ContentControlType, UiOption[]> => {
  const options: Map<ContentControlType, UiOption[]> = new Map();

  ContentControlSettings.forEach(({ type, actions, comingSoonActions }) => {
    const settingOptions: UiOption[] = [];

    const allActions = [...actions, ...(comingSoonActions || [])];

    ControlCategoryActionMapping.forEach(({ header, actions: categoryActions }) => {
      if (categoryActions.some((action) => allActions.includes(action))) {
        settingOptions.push({
          label: header,
          value: header.toLowerCase(),
          meta: { header: true },
        });
        for (const action of categoryActions) {
          if (allActions.includes(action)) {
            settingOptions.push(getSettingOptions(action, comingSoonActions?.includes(action)));
          }
        }
      }
    });

    options.set(type, settingOptions);
  });

  return options;
};
